import {
  Box,
  Button,
  Center,
  Checkbox,
  FadeBorder,
  Icon,
  Image,
  Input,
  InputWrapper,
  Logo,
  PhoneMockup,
  PhoneMockupLayout,
  Text,
} from '@frontend/ui-library';
import { getIdFromUrl, regexes } from '@frontend/utils';
import { isOfframpBill } from '../../utils';
import { SubmitHandler, useForm } from 'react-hook-form';

import { EmailUpdatePageData as LandingPageData } from '../../graphql/generated';

import styles from './Landing.module.css';

import { useEffect } from 'react';
import { useWidgetContext } from '../../context/WidgetContext';
import { useRegisterHotjarPage, useUpdateEmail } from '../../hooks';
import { ReactComponent as ArrowRightIcon } from '@frontend/icons/arrow-right.svg';
import links from '../../constants/links';

interface LandingProps {
  data: LandingPageData;
}

type Inputs = {
  email: string;
  consent: boolean;
  newsletter: boolean;
};

const Landing = ({ data }: LandingProps) => {
  const { fiatAmount, fiatCurrency, expiresAt, merchantName, email, billType } =
    data;
  const { updateEmail, called, loading } = useUpdateEmail();

  const id = getIdFromUrl();

  useRegisterHotjarPage('email-update');

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<Inputs>({
    mode: 'onChange',
    defaultValues: {
      email,
      newsletter: false,
      consent: false,
    },
  });

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    if (!isValid) return;

    await updateEmail({
      variables: {
        id,
        email: data.email,
        type: 'bill',
        subscribeToNewsletter: data.newsletter,
        termsAndConditionsAccepted: data.consent,
      },
    });
  };

  const { configureHeader } = useWidgetContext();

  useEffect(() => {
    configureHeader({
      allowClosePayment: true,
      expiresAt: expiresAt,
      id: id,
      merchantName,
      billType,
    });
  }, [expiresAt]);

  const isOffRamp = isOfframpBill(billType);

  return (
    <PhoneMockupLayout>
      <PhoneMockup className={styles['mockup']}>
        <Text mt="56" mb="0" as="h1" size="big" color="royal-600">
          <Center>
            {!isOffRamp && <>Pay with </>}
            <Logo className={styles['logo']} ml="8" color="royal" width={90} />
          </Center>
        </Text>
        <Center mt="16">
          <Image src="/assets/overlap-tickers.svg" alt="tickers" />
        </Center>
        <FadeBorder mt="32" className={styles['border']} />
        <Text
          mt="32"
          as="div"
          size="tiny-callout"
          color="neutral-500"
          data-test-id="amountToPay"
        >
          {isOffRamp ? 'Amount to off ramp' : 'Amount to be paid'}
        </Text>
        <Text
          mt="12"
          as="div"
          size="massive"
          color="royal-700"
          weight="700"
          data-test-id="fiatAmount"
        >
          {fiatAmount} {fiatCurrency.toUpperCase()}
        </Text>
        <Text
          mt="16"
          as="div"
          size="tiny-callout"
          color="neutral-500"
          className={styles['id-text']}
        >
          <span>{isOffRamp ? 'Off ramp id' : 'Payment id'}:</span>{' '}
          <span>{id}</span>
        </Text>
        <FadeBorder mt="32" className={styles['border']} />
        <div className={styles['form-container']}>
          <Text mt="32" as="div" color="royal-700" size="small">
            We just need your email to inform you of{' '}
            {isOffRamp ? 'transaction' : 'payment'} details and possible
            refunds.
          </Text>
          <Box as="form" mt="16" onSubmit={handleSubmit(onSubmit)}>
            <InputWrapper error={errors.email?.message}>
              <Input
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: regexes.EMAIL,
                    message: 'Please enter a valid email',
                  },
                })}
                error={!!errors?.email}
                label="Your email"
                type="email"
                data-test-id="emailInput"
              />
            </InputWrapper>
            <Checkbox
              mt="16"
              {...register('consent', { required: true })}
              label={
                <>
                  I accept the
                  <Text
                    as="a"
                    color="cerulean-500"
                    decoration="none"
                    target="_blank"
                    rel="noreferrer"
                    size="inherit"
                    href={links.TERMS_OF_SERVICE}
                  >
                    {' '}
                    Terms of Service{' '}
                  </Text>{' '}
                  and{' '}
                  <Text
                    as="a"
                    target="_blank"
                    color="cerulean-500"
                    decoration="none"
                    rel="noreferrer"
                    href={links.PRIVACY_POLICY}
                    size="inherit"
                  >
                    Privacy policy
                  </Text>
                </>
              }
              data-test-id="emailCheckbox"
            />
            <Checkbox
              mt="16"
              {...register('newsletter')}
              label="Subscribe to xMoney's newsletter"
            />
            <Button
              mt="16"
              fullWidth
              type="submit"
              loading={loading || called}
              disabled={!isValid}
              data-test-id="payButton"
              rightIcon={<Icon component={<ArrowRightIcon />} />}
            >
              continue
            </Button>
          </Box>
        </div>
      </PhoneMockup>
    </PhoneMockupLayout>
  );
};

export default Landing;
